import { ModelBase, ObservableCollection, RepositorySaveResponse } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryDocumentKind, InventoryEffect, LabeledEnumValue, OrderDocumentKind, PurchaseDocumentKind, SaleDocumentKind, SplitPaymentState } from '@skylab-shared/dataModel';
import { PickByType } from '@skylab-shared/types';
import { withModule } from '../availability/module-availability';
import { NumberingItem } from '../numbering/queries/numbering.query-base';
import { VatRateItem } from '../vat/queries/vat-rate-item';

export type DocumentRepositorySaveResponse = RepositorySaveResponse<{
    documentNumber: string;
}>;

export type SaleDocumentRepositorySaveResponse = RepositorySaveResponse<{
    documentNumber: string;
    isKsefSchemaInvalid?: boolean;
}>;


export type DocumentWithExchangeRate = {
    calculationMethod: CalculationMethod;
    currencyId: number;
    exchangeRate?: ExchangeRateModel;
} & ModelBase;

const documentKindOrder = [
    SaleDocumentKind.ProForma,
    SaleDocumentKind.Invoice,
    SaleDocumentKind.InvoiceAdjustment,
    SaleDocumentKind.Receipt,
    SaleDocumentKind.ReceiptAdjustment,
    SaleDocumentKind.AdvanceInvoice,
    SaleDocumentKind.AdvanceInvoiceAdjustment,
    SaleDocumentKind.VatMarginInvoice,
    SaleDocumentKind.VatMarginInvoiceAdjustment,
    PurchaseDocumentKind.Expense,
    PurchaseDocumentKind.PurchaseInvoice,
    PurchaseDocumentKind.PurchaseInvoiceAdjustment,
    PurchaseDocumentKind.EPurchaseInvoice,
    InventoryDocumentKind.GoodsIssue,
    InventoryDocumentKind.GoodsIssueAdjustment,
    InventoryDocumentKind.InternalGoodsIssue,
    InventoryDocumentKind.GoodsReceipt,
    InventoryDocumentKind.GoodsReceiptAdjustment,
    InventoryDocumentKind.InternalGoodsReceipt,
    InventoryDocumentKind.Stocktaking,
    OrderDocumentKind.Order
];

export function sortDocumentKinds<T = LabeledEnumValue>(array: T[], key: string): T[] {
    return array.sort((item1, item2) => documentKindOrder.indexOf(<SaleDocumentKind> (item1 as any)[key]) - documentKindOrder.indexOf(<SaleDocumentKind> (item2 as any)[key]));
}

export function sortVatRates(array: VatRateItem[], key: keyof PickByType<VatRateItem, number> = 'order'): VatRateItem[] {
    return array.sort((r1, r2) => r1[key] - r2[key]);
}

const splitPaymentCheckedStates = [SplitPaymentState.Manual, SplitPaymentState.True];
export const splitPaymentChecked = (sp: SplitPaymentState) => splitPaymentCheckedStates.includes(sp);

export const isDocumentWithIncludedInventoryItems = (model: ModelBase & { items: ObservableCollection<ModelBase & {includesInventoryControl: boolean}> }) => model.items.some(elem => elem.includesInventoryControl);

export const getCorrectInventoryEffectFromSourceDocument = (previousInventoryEffect: InventoryEffect | null): InventoryEffect | null => {
    if (!previousInventoryEffect) return null;
    if (!withModule('Inventory')) return InventoryEffect.NotRealized;

    return previousInventoryEffect;
};

export const getCorrectNumberingIdFromSourceDocument = (previousNumberingId: number, numberings: NumberingItem[], kind: SaleDocumentKind | PurchaseDocumentKind | InventoryDocumentKind) => {
    if (numberings.find(elem => elem.id === previousNumberingId)?.active) return previousNumberingId;

    const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.indexOf(kind) > -1);
    return defaultNumberingId;
};

export const isPurchaseDocument = (kind: PurchaseDocumentKind | SaleDocumentKind | InventoryDocumentKind) : boolean => {
    return [PurchaseDocumentKind.EPurchaseInvoice, PurchaseDocumentKind.Expense, PurchaseDocumentKind.PurchaseInvoice, PurchaseDocumentKind.PurchaseInvoiceAdjustment].includes(kind as PurchaseDocumentKind);
};
